import { type ReactElement } from 'react';
import type { VersionInfo } from './types';

interface AppVersionFooterProps {
  current: VersionInfo;
  /** Extra bottom margin (px) so the stamp clears a fixed bottom nav bar once scrolled into view (0 for the CRA app). */
  bottomOffset?: number;
}

/**
 * The build stamp, rendered as a normal page footer — the last thing in
 * document flow, below whatever page is currently mounted. It is NOT pinned
 * to the viewport: scroll past the page's own content to see it, same as any
 * other footer. It previously used `position: fixed` and floated over page
 * content on every screen at all times, which read as permanent on-screen
 * chrome rather than build metadata.
 *
 * LEADS WITH THE BUILD DATE AND SHA, NOT THE VERSION. The `version` field is
 * now date-based (`YYYY.MM.DD`, see scripts/app-version/gen-version.mjs) — it
 * previously sat on a static `package.json` "1.0.0" that never moved, which made
 * the stamp read as "nothing shipped" after a successful deploy and sent people
 * hunting for a broken pipeline. The chip still leads with the sha + build date
 * because the sha is the true build-identity key (what update detection keys on)
 * and the date directly answers "am I on today's build?" without a commit lookup.
 *
 * The version field must stay semver-ish: `isValidVersionInfo` requires it and
 * FAILS CLOSED, so the date form is deliberately `\d+.\d+.\d+` — a non-numeric
 * reformat would silently disable update detection for everyone.
 */
export function AppVersionFooter({
  current,
  bottomOffset = 0,
}: AppVersionFooterProps): ReactElement {
  const builtAtMs = Date.parse(current.builtAt);
  const builtAtValid = !Number.isNaN(builtAtMs);
  /** Full timestamp for the hover tooltip. */
  const builtAtLabel = builtAtValid
    ? new Date(builtAtMs).toLocaleString()
    : current.builtAt;
  /**
   * Compact date for the chip itself, e.g. "Aug 25, 5:39 AM".
   *
   * No year, deliberately: this chip answers "am I on today's build?", and a build old
   * enough for the year to be ambiguous is a far bigger problem than a mislabelled chip.
   * The hover title carries the full, unambiguous timestamp for support.
   */
  const builtAtShort = builtAtValid
    ? new Date(builtAtMs).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        // The zone is not decoration. docs/deploy-log.md and the deploy pins are UTC,
        // and this chip exists so support can cross-reference against them — without a
        // zone the two speak different clocks, off by hours and by a whole DAY near
        // midnight, which is precisely when a "did it deploy?" question gets asked.
        timeZoneName: 'short',
      })
    : null;

  return (
    <footer
      className="select-none px-4 py-3 text-right"
      style={{ marginBottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))` }}
      aria-hidden="true"
    >
      <span
        className="rounded bg-white/70 px-1.5 py-0.5 text-[11px] leading-none text-slate-500 tabular-nums"
        title={`${current.app} · v${current.version} · ${current.sha} · built ${builtAtLabel}`}
      >
        {current.sha}
        {builtAtShort ? ` · ${builtAtShort}` : ''}
      </span>
    </footer>
  );
}
